/**
 * The share link's fragment (`/s/<id>#<passphrase>`) is the decryption key.
 * Browsers never send a fragment to a server, but everything running *in*
 * the page can read `location.href` — analytics and error SDKs, extensions,
 * anything that snapshots the URL — and the address bar keeps it visible for
 * screenshots, browser history, and tab sync across devices.
 *
 * So the key is read once, before the app renders, and immediately erased
 * from the URL with `history.replaceState`. From that point on
 * `location.href` is just `/s/<id>`, and the only copy of the passphrase is
 * the one in this module's closure — which is the property that makes any
 * later telemetry safe by construction rather than by scrubbing.
 *
 * The cost, deliberately accepted: a reload no longer carries the key, so
 * the recipient has to open the original link again. RetrieveSecretPage's
 * "invalid link" copy says so.
 */
interface LocationLike {
  hash: string;
  pathname: string;
  search: string;
}

interface HistoryLike {
  replaceState(data: unknown, unused: string, url: string): void;
}

let key: string | null = null;
let captured = false;

/**
 * Takes the key out of the URL. Safe to call more than once — React's
 * StrictMode double-invokes effects in development, and only the first call
 * can find anything anyway.
 */
export function captureLinkKey(
  location: LocationLike = window.location,
  history: HistoryLike = window.history,
): string | null {
  if (captured) return key;
  captured = true;

  const hash = location.hash;
  if (!hash || hash.length < 2) return null;

  key = decodeURIComponent(hash.slice(1));
  // Same document, same entry, minus the fragment: no navigation, no
  // history entry, and no re-render of the router's current location.
  history.replaceState(null, "", `${location.pathname}${location.search}`);
  return key;
}

/** The captured key, or null if this URL never had one. */
export function linkKey(): string | null {
  return key;
}

/** Test seam — there is one URL per page load in a real browser. */
export function resetLinkKeyForTesting(): void {
  key = null;
  captured = false;
}
